import { createContext, useEffect, useState, useCallback } from "react";
import * as signalR from "@microsoft/signalr";
import notificationAPI from "../services/api/notificationAPI";
import { notificationURL } from "../services/api/config";
import chatApi from "../services/api/chatAPI";

export const NotificationContext = createContext();

const NotificationProvider = ({ children }) => {
    const [notifications, setNotifications] = useState([]);
    const [unReadMessages, setUnReadMessages] = useState([]);
    const [connection, setConnection] = useState(null);
    const [token, setToken] = useState(localStorage.getItem("accessToken"));
    const [orderUpdated, setOrderUpdated] = useState(false);

    // Function to refresh notifications from API
    const refreshNotifications = async () => {
        const response = await notificationAPI.GetNotification();
        if (response.status) {
            setNotifications((prevNotifications) =>
                response.resultData.map((notification, index) => ({
                    ...notification,
                    key: index + 1,
                }))
            );
        }
    };

    const fetchChatHistory = async () => {
        try {
            const response = await chatApi.GetChat({ limit: 500, offset: 0 });
            if (response?.status && response.resultData) {
                setUnReadMessages(
                    response.resultData.map((msg) => ({
                        id: msg.messageId,
                        text: msg.message,
                        sender: msg.senderType === "customer" ? "user" : "agent",
                        timestamp: msg.sentAt,
                        isRead: msg.isRead,
                    }))
                );
            }
        } catch (error) {
            console.log("Error fetching chat history:", error);
        } finally {
        }
    };

    // Trigger orderUpdated state when notification is received
    const handleNotification = useCallback(async (message) => {
        // Check if the notification is related to orders
        const isOrderNotification = typeof message === 'string' && message.toLowerCase().includes('payment');

        // Update orderUpdated state if notification is order-related
        if (isOrderNotification) {
            setOrderUpdated(true);
        }

        // Refresh notifications to get the complete notification object
        await refreshNotifications();
        await fetchChatHistory();
    }, []);


    useEffect(() => {
        const handleTokenChange = () => {
            setToken(localStorage.getItem("accessToken"));
        };

        window.addEventListener("accessTokenUpdated", handleTokenChange);
        window.addEventListener("storage", handleTokenChange);

        return () => {
            window.removeEventListener("accessTokenUpdated", handleTokenChange);
            window.removeEventListener("storage", handleTokenChange);
        };
    }, []);

    useEffect(() => {
        if (!token) {
            console.warn("No access token available. Skipping SignalR connection.");
            return;
        }

        const initializeNotificationHub = async () => {
            try {
                await refreshNotifications(); // Fetch notifications on load
                await fetchChatHistory();
                const newConnection = new signalR.HubConnectionBuilder()
                    .withUrl(notificationURL, {
                        accessTokenFactory: () => token,
                        withCredentials: true,
                    })
                    .withAutomaticReconnect()
                    .build();

                await newConnection.start();
                console.log("Connected to Notification Hub");

                newConnection.on("ReceiveNotification", handleNotification);

                setConnection(newConnection);
            } catch (error) {
                console.error("Error initializing SignalR connection:", error);
            }
        };

        initializeNotificationHub();

        return () => {
            if (connection) {
                connection.stop().then(() => console.log("SignalR connection stopped."));
            }
        };
    }, [token, handleNotification]);

    // Reset the orderUpdated flag once it's been consumed
    const resetOrderUpdated = useCallback(() => {
        setOrderUpdated(false);
    }, []);

    return (
        <NotificationContext.Provider value={{
            notifications, setNotifications,
            refreshNotifications, unReadMessages, fetchChatHistory,
            orderUpdated, resetOrderUpdated
        }}>
            {children}
        </NotificationContext.Provider>
    );
};

export default NotificationProvider;
